import ActionsDropdown from '@anm/components/ActionsDropdown';
import { Wrapper as BadgeWrapper } from '@anm/components/Badge';
import { DropdownWrapper } from '@anm/components/Dropdown';
import Loader from '@anm/components/Loader';
import Tooltip, { TooltipWrapper as TooltipContentWrapper } from '@anm/components/Tooltip';
import getFromCdn from '@anm/helpers/getFromCdn';
import {
  dashboardDescriptionStyles,
  dashboardTitleBlockStyles,
  fadeOutAnimation,
  flexCenter,
  media,
  myProjectsItemBottomStyles,
  stretchBlock
} from '@anm/styles/helpers';
import styled, { css } from 'styled-components';

import ActionItemWrapper from '../ActionsDropdown/ActionItem/Wrapper';
import { Wrapper as ProgressiveVideoPreviewWrapper } from '../video/ProgressiveVideoPreview';

const centeredWrapper = css`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 126;
  ${flexCenter};
`;

const mobileBadgeStyles = media.phone`
  width: 22px;
  height: 22px;
  &:before {
    width: 12px;
    height: 12px;
  }
`;

export const TopPlayerStatusesWrapper = styled.div`
  position: absolute;
  z-index: 133;
  top: 0;
  right: 0;
  > ${TooltipContentWrapper} {
    padding: 10px;
  }
  ${BadgeWrapper} {
    margin: 0;
    padding: 0;
    width: 36px;
    height: 36px;
    cursor: pointer;
    background: #fff;
    border-radius: 5px;
    box-shadow: 0 2px 10px 0 rgba(1, 83, 130, 0.1);
    span {
      display: none;
    }
    &:before {
      margin: 0;
      width: 16px;
      height: 16px;
    }

    ${mobileBadgeStyles};
  }
`;

export const TooltipWrapper = styled(Tooltip)`
  padding: 4px 10px 7px;
  margin-top: 8px;
`;

export const TooltipContent = styled.span`
  color: #fff;
  font-size: 13px;
  line-height: 16px;
`;

const playerWrapperAdaptiveStyles = media.tablet`
  max-width: 100%;
`;

const playerDescriptionAdaptiveStyles = media.tablet`
  margin-top: 9px;
`;

export const PlayerBottom = styled.div`
  ${myProjectsItemBottomStyles};
  padding-bottom: 10px;
  ${DropdownWrapper} {
    margin-top: auto;
  }
`;

export const PlayerDuration = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  height: 19px;
  border-radius: 5px;
  background-color: rgba(41, 42, 43, 0.6);
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  line-height: 15px;
  padding: 2px 4px;
  z-index: 128;
  letter-spacing: -0.02em;
`;

export const PlayerPreview = styled.div`
  position: relative;
  border-radius: 5px 5px 0 0;
  overflow: hidden;
  min-height: 50px;
  box-sizing: border-box;
  background: url(${getFromCdn('images/dashboard/default_preview.svg')}) no-repeat center #ebeef5;
  cursor: pointer;

  &:after {
    display: block;
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 129;
    pointer-events: none;
    border: 1px solid rgba(1, 83, 130, 0.2);
    border-radius: 5px 5px 0 0;
  }
  ${ProgressiveVideoPreviewWrapper} {
    border-radius: 5px 5px 0 0;
  }
`;

export const PlayerTitleBlock = styled.div<{ disabled?: boolean }>`
  ${dashboardTitleBlockStyles('#292a2b')};

  ${({ disabled }) =>
    disabled &&
    css`
      color: #919eaa;
      cursor: default;
      pointer-events: none;
    `};
`;

export const PlayerDescription = styled.p`
  ${dashboardDescriptionStyles};
  ${playerDescriptionAdaptiveStyles};
`;

export const PlayerTag = styled.span`
  position: absolute;
  z-index: 125;
  top: 10px;
  left: 10px;
  background-color: #fff200;
  padding: 0 10px;
  font-size: 14px;
  font-weight: bold;
  line-height: 29px;
  border-radius: 3px;
`;

export const LoaderWrapper = styled.div`
  ${stretchBlock}
  ${flexCenter}
  z-index: 130;
`;

export const LoaderOverlay = styled.div`
  border-radius: 5px;
  background-color: rgba(41, 42, 43, 0.6);
  height: 86px;
  width: 86px;
`;

export const CenteredLoader = styled(Loader)`
  margin-top: 43px;
  margin-left: 15px;
`;

const playerStatusesWrapperOnMobile = media.phone`
  margin-top: 7px;
  padding-left: 5px;
  &:not(:empty) {
    margin-bottom: -3px;
  }
`;

export const PlayerStatusesWrapper = styled.div`
  width: 100%;
  display: flex;
  margin-top: 8px;
  padding: 0 5px 0 4px;
  &:not(:empty) {
    margin-bottom: -6px;
  }
  ${playerStatusesWrapperOnMobile};
`;

export const ProgressWrapper = styled.div`
  ${centeredWrapper};
  z-index: 131;
  background: linear-gradient(0deg, rgba(64, 68, 73, 0.6), rgba(64, 68, 73, 0.6));
`;

export const SuccessIcon = styled.div<{ bottomHeight?: number }>`
  ${centeredWrapper};
  top: 20px;
  pointer-events: none;

  ${({ bottomHeight }) =>
    bottomHeight &&
    css`
      transform: translateY(calc(-${bottomHeight / 2}px));
    `}

  &:after {
    ${fadeOutAnimation};
    display: block;
    content: '';
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: url(${getFromCdn('images/icons/success_upload_icon.svg')}) no-repeat center, #0c9744;
    animation-duration: 0.5s;
    animation-name: fadeOut;
    animation-delay: 1.5s;
    animation-fill-mode: forwards;
  }
`;

export const PlayerActionsDropdown = styled(ActionsDropdown)`
  margin: 0;
  ${ActionItemWrapper} {
    min-width: 255px;
  }
`;

export const PlayerModifiedDateAndActionsWrapper = styled.div`
  ${flexCenter};
  justify-content: space-between;
`;

export const PlayerWrapper = styled.div`
  position: relative;
  max-width: 280px;
  box-shadow: 0 1px 5px rgba(41, 42, 43, 0.1);
  border-radius: 5px;
  ${playerWrapperAdaptiveStyles};
`;
